import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";

import { CtaBand } from "../components/CtaBand";
import { diensten } from "../lib/data";
import { dienstContent } from "../lib/diensten-content";
import { dienstGalerij } from "../lib/dienst-galerij";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";
import { getKostenContent } from "../lib/kosten.functions";

export const Route = createFileRoute("/diensten_/$slug")({
  loader: async ({ params }) => {
    const dienst = diensten.find((d) => d.slug === params.slug);
    const content = dienstContent[params.slug];
    if (!dienst || !content) throw notFound();
    const kosten = await getKostenContent();
    const kostenItem = kosten.items.find((i) => i.slug === params.slug) ?? null;
    return { dienst, content, kostenItem, kostenDisclaimer: kosten.disclaimer };
  },
  head: ({ params }) => {
    const dienst = diensten.find((d) => d.slug === params.slug);
    const content = dienstContent[params.slug];
    if (!dienst || !content) return {};
    const title = `${dienst.title} in Dordrecht | Fedor Timmerwerken`;
    return {
      meta: [
        { title },
        { name: "description", content: content.metaDescription },
        { property: "og:title", content: title },
        { property: "og:description", content: content.metaDescription },
        { property: "og:url", content: `${SITE_URL}/diensten/${dienst.slug}` },
        ...ogImageMeta(ogImageDefault, dienst.imageAlt),
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/diensten/${dienst.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: dienst.title,
            description: content.metaDescription,
            serviceType: dienst.title,
            url: `${SITE_URL}/diensten/${dienst.slug}`,
            areaServed: { "@type": "City", name: "Dordrecht" },
            provider: {
              "@type": "HomeAndConstructionBusiness",
              name: "Fedor Timmerwerken",
              url: SITE_URL,
              telephone: "+31628130786",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Dordrecht",
                addressCountry: "NL",
              },
            },
          }),
        },
      ],
    };
  },
  component: DienstDetailPage,
});

function DienstDetailPage() {
  const { dienst, content, kostenItem, kostenDisclaimer } = Route.useLoaderData();
  const overige = diensten.filter((d) => d.slug !== dienst.slug).slice(0, 3);
  const galerij = dienstGalerij[dienst.slug] ?? [];

  return (
    <>
      <div className="py-12 sm:py-16 lg:py-24">
        <div className="container-tight">
          <Link
            to="/diensten"
            className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Alle diensten
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <span className="font-heading text-sm font-semibold uppercase tracking-wider text-primary">
                Dienst
              </span>
              <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {dienst.title}
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{content.intro}</p>

              {content.paragraphs.map((p: string) => (
                <p key={p.slice(0, 24)} className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}

              <ul className="mt-8 space-y-2">
                {content.highlights.map((h: string) => (
                  <li key={h} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    {h}
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
                >
                  Gratis offerte aanvragen
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border/60">
              <img
                src={dienst.imageSrc}
                srcSet={`${dienst.imageSrc768} 768w, ${dienst.imageSrc} 1200w`}
                sizes="(min-width: 1024px) 560px, calc(100vw - 2rem)"
                alt={dienst.imageAlt}
                width={1200}
                height={900}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          {galerij.length > 0 && (
            <div className="mt-20">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Projecten in beeld
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Een greep uit eerder uitgevoerde {dienst.title.toLowerCase()}-projecten.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
                {galerij.map((src, i) => (
                  <div
                    key={src}
                    className="aspect-[4/3] overflow-hidden rounded-xl border border-border/60 bg-muted"
                  >
                    <img
                      src={src}
                      alt={`${dienst.title} project ${i + 1} door Fedor Timmerwerken`}
                      width={1000}
                      height={750}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            id="kosten"
            className="mt-16 scroll-mt-24 rounded-2xl border border-border/60 bg-card p-6 sm:p-8"
          >
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
              Wat kost {dienst.title.toLowerCase()}?
            </h2>
            <p className="mt-3 font-heading text-2xl font-bold leading-tight tracking-tight text-primary sm:text-3xl">
              {kostenItem?.prijs ?? "Prijs op aanvraag"}
            </p>
            {kostenItem?.prijsNoot && (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {kostenItem.prijsNoot}
              </p>
            )}

            {kostenItem && kostenItem.factoren.length > 0 && (
              <>
                <h3 className="mt-6 font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Prijs hangt af van
                </h3>
                <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground/80">
                  {kostenItem.factoren.map((factor: string) => (
                    <li key={factor} className="flex items-start gap-2.5">
                      <CheckCircle
                        className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <span className="min-w-0">{factor}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
              {kostenDisclaimer}
            </p>

            <div className="mt-5 flex flex-col gap-2 border-t border-border/60 pt-4 text-sm font-semibold sm:flex-row sm:gap-6">
              <Link
                to="/contact"
                className="inline-flex min-h-11 items-center gap-1 text-primary hover:text-primary/80 sm:min-h-0"
              >
                Vraag een prijs op maat aan
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              </Link>
              {dienst.slug === "aanbouw" && (
                <Link
                  to="/aanbouw-kosten"
                  className="inline-flex min-h-11 items-center gap-1 text-foreground hover:text-primary sm:min-h-0"
                >
                  Kostengids: richtprijzen per m²
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
              )}
              {dienst.slug === "dakkapel" && (
                <Link
                  to="/dakkapel-kosten"
                  className="inline-flex min-h-11 items-center gap-1 text-foreground hover:text-primary sm:min-h-0"
                >
                  Kostengids: wat kost een dakkapel?
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
              )}
              {dienst.slug === "kozijnen" && (
                <Link
                  to="/kozijnen-kosten"
                  className="inline-flex min-h-11 items-center gap-1 text-foreground hover:text-primary sm:min-h-0"
                >
                  Kostengids: wat kost een kozijn?
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
              )}
              {dienst.slug === "renovatie" && (
                <Link
                  to="/renovatie-kosten"
                  className="inline-flex min-h-11 items-center gap-1 text-foreground hover:text-primary sm:min-h-0"
                >
                  Kostengids: wat kost een renovatie?
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
              )}
            </div>
          </div>



          <div className="mt-20">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
              Andere diensten
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {overige.map((d) => (
                <Link
                  key={d.slug}
                  to="/diensten/$slug"
                  params={{ slug: d.slug }}
                  className="group rounded-xl border border-border/60 bg-card p-5 transition-colors hover:border-primary/30"
                >
                  <span className="font-heading text-base font-semibold text-foreground">
                    {d.title}
                  </span>
                  <span className="mt-2 flex items-center gap-1 text-sm font-semibold text-primary">
                    Bekijk dienst
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}
